import { html, type TemplateResult } from 'lit';

/**
 * Reusable skeleton loaders. Lit-template helpers (no custom element overhead).
 *
 * Usage:
 *   ${skeletonLine({ width: 'w-32' })}
 *   ${skeletonRow()}
 *   ${skeletonCard()}
 *   ${skeletonStatGrid(5)}
 */

export function skeletonLine(opts: { width?: string; height?: string; className?: string } = {}): TemplateResult {
  const { width = 'w-full', height = 'h-4', className = '' } = opts;
  return html`<div class="skeleton ${width} ${height} ${className}"></div>`;
}

export function skeletonCard(): TemplateResult {
  return html`
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body gap-3">
        ${skeletonLine({ width: 'w-1/2', height: 'h-5' })}
        ${skeletonLine({ width: 'w-3/4' })}
        ${skeletonLine({ width: 'w-2/3' })}
      </div>
    </div>
  `;
}

export function skeletonRow(): TemplateResult {
  return html`
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body flex-row items-center gap-4 py-4">
        <div class="flex flex-1 flex-col gap-2">
          ${skeletonLine({ width: 'w-48', height: 'h-5' })}
          ${skeletonLine({ width: 'w-32', height: 'h-3' })}
        </div>
        <div class="flex gap-6">
          ${skeletonLine({ width: 'w-10', height: 'h-8' })}
          ${skeletonLine({ width: 'w-10', height: 'h-8' })}
          ${skeletonLine({ width: 'w-10', height: 'h-8' })}
          ${skeletonLine({ width: 'w-10', height: 'h-8' })}
        </div>
      </div>
    </div>
  `;
}

export function skeletonRows(count = 3): TemplateResult {
  return html`<div class="flex flex-col gap-3">${Array.from({ length: count }, () => skeletonRow())}</div>`;
}

// The grid classes are written out, not built from `count`: Tailwind only emits
// classes it can read in the source, and `sm:grid-cols-${count}` was never
// generated — the skeleton always fell back to two columns.
export function skeletonStatCards(count = 4): TemplateResult {
  return html`
    <div class="grid grid-cols-2 gap-6 lg:grid-cols-4">
      ${Array.from(
        { length: count },
        () => html`
          <div class="card bg-base-100">
            <div class="card-body gap-2 p-6">
              ${skeletonLine({ width: 'w-20', height: 'h-3' })}
              ${skeletonLine({ width: 'w-16', height: 'h-11' })}
            </div>
          </div>
        `,
      )}
    </div>
  `;
}
